import React, { ImgHTMLAttributes } from 'react';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { GlobalPropsWithChildren } from 'src/types';

import styles from './styles.module.css';

export type PlaceholderProps = {
  name?: string;
  children?: React.ReactNode;
  paragraphs?: number;
  short?: boolean;
  lines?: number;
};

export interface CompoundedComponent
  extends React.ForwardRefExoticComponent<
    PlaceholderProps & React.RefAttributes<HTMLDivElement>
  > {
  Line: React.FC<PlaceholderProps>;
  Paragraph: React.FC<PlaceholderProps>;
  Image: React.FC<GlobalPropsWithChildren & ImgHTMLAttributes<HTMLElement>>;
}

export const Placeholder = React.forwardRef<HTMLDivElement, PlaceholderProps>(
  function PlaceholderComponent(props: PlaceholderProps, ref) {
    const { children, name = 'placeholder' } = props;

    return (
      <div ref={ref} data-component={useSlug(name)}>
        {children}
      </div>
    );
  }
) as CompoundedComponent;

function PlaceholderLineComponent({
  name = 'placeholder-line',
  short = false,
}: PlaceholderProps) {
  return (
    <div
      className={classes([styles.shimmeringLine, short && [styles.short]])}
      data-component={useSlug(name)}
    />
  );
}

Placeholder.Line = PlaceholderLineComponent;
Placeholder.Line.displayName = 'Placeholder.Line';

Placeholder.Paragraph = ({
  name = 'placeholder-paragraph',
  paragraphs = 1,
  lines = 6,
}: PlaceholderProps) => {
  const paragraphArray = [...Array(paragraphs).keys()];
  const paragraphLines = [...Array(lines).keys()];

  return (
    <div className={styles.placeholderParagraphWrapper}>
      {paragraphArray.map((i) => (
        <div
          key={i}
          className={styles.shimmeringParagraph}
          data-component={useSlug(name)}
        >
          {paragraphLines.map((j) => (
            <Placeholder.Line key={j} />
          ))}
        </div>
      ))}
    </div>
  );
};
Placeholder.Paragraph.displayName = 'Placeholder.Paragraph';

function PlaceholderImageComponent({
  name = 'placeholder-image',
  ...props
}: GlobalPropsWithChildren) {
  return (
    <div
      className={classes([styles.shimmeringImage], props)}
      data-component={useSlug(name)}
    />
  );
}

Placeholder.Image = PlaceholderImageComponent;
Placeholder.Image.displayName = 'Placeholder.Image';
